import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, animate, useInView, useReducedMotion } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote, Terminal } from 'lucide-react';
interface Stat {
  value: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  label: string;
}
const STATS: Stat[] = [{
  value: 500,
  suffix: '+',
  label: 'ENG_TEAMS'
}, {
  value: 50000,
  suffix: '+',
  label: 'DEVS_MEASURED'
}, {
  value: 30,
  suffix: '%',
  label: 'AVG_SPEND_RECLAIMED'
}, {
  value: 4.8,
  suffix: '/5',
  decimals: 1,
  label: 'LEADER_RATING'
}];
const LOGOS = ['NORTHWIND', 'HELIO', 'APEX_LABS', 'QUANTA', 'BEACON', 'STRATUS', 'VERTEX'];
interface Testimonial {
  quote: string;
  name: string;
  title: string;
  company: string;
}
const TESTIMONIALS: Testimonial[] = [{
  quote: 'We were paying for 500 Copilot Enterprise seats. TokenMeter showed us 158 were idle. Reclaiming them cut our AI tooling bill by a third in the first quarter.',
  name: 'Dana Whitfield',
  title: 'VP of Engineering',
  company: 'Northwind'
}, {
  quote: "Mixing Cursor, Claude Code, and Copilot across teams made spend impossible to reason about. Waydev's TokenMeter gave us one number we could actually take to finance.",
  name: 'Marcus Lee',
  title: 'Director of Platform Engineering',
  company: 'Helio'
}, {
  quote: 'The token-spike alerts alone paid for themselves. We caught a runaway context-window job before it turned into a five-figure surprise.',
  name: 'Priya Nair',
  title: 'CTO',
  company: 'Apex Labs'
}, {
  quote: 'Finally, AI tool ROI we can defend. Tying accepted completions to PR cycle time changed how our leadership talks about AI investment.',
  name: 'Tom Brennan',
  title: 'Head of Developer Productivity',
  company: 'Quanta'
}];
function AnimatedStat({
  stat
}: {
  stat: Stat;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inViewRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(inViewRef, {
    once: true,
    margin: '-80px'
  });
  const reduceMotion = useReducedMotion();
  const format = (v: number) => `${stat.prefix ?? ''}${v.toLocaleString('en-US', {
    minimumFractionDigits: stat.decimals ?? 0,
    maximumFractionDigits: stat.decimals ?? 0
  })}${stat.suffix ?? ''}`;
  useEffect(() => {
    if (!isInView) return;
    if (reduceMotion) {
      if (ref.current) ref.current.textContent = format(stat.value);
      return;
    }
    const controls = animate(0, stat.value, {
      duration: 1.4,
      ease: 'easeOut',
      onUpdate: v => {
        if (ref.current) ref.current.textContent = format(v);
      }
    });
    return controls.stop;
  }, [isInView, reduceMotion]);
  return <div ref={inViewRef} className="bg-slate-950 border border-slate-800 p-6 text-center flex flex-col justify-center" data-id="element-80">
      <div className="text-3xl md:text-4xl font-mono font-bold text-emerald-400 glow-text-emerald mb-2" data-id="element-81">
        <span ref={ref} data-id="element-82">{format(0)}</span>
      </div>
      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest" data-id="element-83">
        {stat.label}
      </div>
    </div>;
}
export function WaydevProof() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduceMotion = useReducedMotion();
  const go = (dir: number) => setActive(prev => (prev + dir + TESTIMONIALS.length) % TESTIMONIALS.length);
  useEffect(() => {
    if (paused || reduceMotion) return;
    const id = setInterval(() => {
      setActive(prev => (prev + 1) % TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(id);
  }, [paused, reduceMotion]);
  const t = TESTIMONIALS[active];
  return <section className="relative z-10 bg-slate-950 border-b border-slate-800 flex flex-col lg:flex-row" data-id="element-84">
      {/* Left Column: Stats & Logos */}
      <div className="flex-1 border-r border-slate-800 flex flex-col" data-id="element-85">
        <div className="p-8 md:p-12 border-b border-slate-800 bg-slate-900/20" data-id="element-86">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-mono font-bold uppercase tracking-widest mb-6" data-id="element-87">
            <Terminal size={12} data-id="element-88" /> SYS_CREDENTIALS
          </div>
          <h2 className="text-3xl font-bold text-slate-50 mb-4 tracking-tight" data-id="element-89">
            Powered by Waydev Intelligence
          </h2>
          <p className="text-slate-400 leading-relaxed font-mono text-sm max-w-xl" data-id="element-90">
            TokenMeter runs on the telemetry engine trusted by top engineering
            orgs to track DORA metrics and resource allocation.
          </p>
        </div>

        <div className="grid grid-cols-2 flex-1" data-id="element-91">
          {STATS.map((stat, i) => <div key={stat.label} className={i % 2 === 0 ? 'border-r border-slate-800' : ''} data-id="element-92">
              <div className={i < 2 ? 'border-b border-slate-800 h-full' : 'h-full'} data-id="element-93">
                <AnimatedStat stat={stat} data-id="element-94" />
              </div>
            </div>)}
        </div>

        <div className="p-8 border-t border-slate-800 bg-slate-900/50" data-id="element-95">
          <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-6" data-id="element-96">
            &gt; ACTIVE_NODES
          </p>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-97">
            {LOGOS.map(logo => <span key={logo} className="text-sm font-mono font-bold text-slate-600 hover:text-slate-300 transition-colors cursor-default" data-id="element-98">
                {logo}
              </span>)}
          </div>
        </div>
      </div>

      {/* Right Column: Testimonials */}
      <div className="flex-1 p-8 md:p-12 flex flex-col justify-center relative bg-slate-950" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-99">
        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 blur-[100px] pointer-events-none rounded-full" data-id="element-100"></div>

        <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-8 flex items-center gap-2" data-id="element-101">
          <Quote size={12} className="text-emerald-500" data-id="element-102" /> USER_LOGS
        </div>

        <div className="relative z-10 min-h-[200px]" aria-live="polite" data-id="element-103">
          <AnimatePresence mode="wait" data-id="element-104">
            <motion.figure key={active} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} data-id="element-105">
              <blockquote className="text-xl text-slate-200 leading-relaxed font-mono mb-8" data-id="element-106">
                &gt; "{t.quote}"
              </blockquote>
              <figcaption className="flex items-center justify-between flex-wrap gap-4 border-t border-slate-800 pt-6" data-id="element-107">
                <div data-id="element-108">
                  <div className="font-bold text-slate-50" data-id="element-109">{t.name}</div>
                  <div className="text-xs font-mono text-slate-500 mt-1" data-id="element-110">
                    {t.title} @ {t.company}
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 uppercase tracking-widest" data-id="element-111">
                  <Star size={10} className="fill-emerald-400" data-id="element-112" /> VERIFIED
                </span>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-4 mt-12" data-id="element-113">
          <button onClick={() => go(-1)} className="p-2 bg-slate-900 border border-slate-800 text-slate-400 hover:text-emerald-400 hover:border-emerald-500 transition-colors" data-id="element-114">
            <ChevronLeft size={16} data-id="element-115" />
          </button>
          <div className="flex items-center gap-2" data-id="element-116">
            {TESTIMONIALS.map((item, idx) => <button key={item.name} onClick={() => setActive(idx)} className={`h-1 transition-all ${active === idx ? 'w-6 bg-emerald-500 glow-box-emerald' : 'w-2 bg-slate-800 hover:bg-slate-600'}`} data-id="element-117" />)}
          </div>
          <button onClick={() => go(1)} className="p-2 bg-slate-900 border border-slate-800 text-slate-400 hover:text-emerald-400 hover:border-emerald-500 transition-colors" data-id="element-118">
            <ChevronRight size={16} data-id="element-119" />
          </button>
        </div>
      </div>
    </section>;
}